import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { projectId } from "@/sanity/env";
import { ReadonlyURLSearchParams } from "next/navigation";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sanityImageUrl(imageString: string): string {
  if (!imageString.startsWith("image-")) {
    throw new Error("Invalid image string");
  }

  // Remove the 'image-' prefix
  const strippedString = imageString.substring(6);

  // Split the remaining string by '-'
  const parts = strippedString.split("-");

  if (parts.length < 3) {
    throw new Error("Invalid image string format");
  }

  const id = parts[0];
  const dimensions = parts[1];
  const format = parts.slice(2).join("-"); // Handle formats with dashes

  // Construct the filename
  const filename = `${id}-${dimensions}.${format}`;

  // Build the URL
  return `https://cdn.sanity.io/images/${projectId}/production/${filename}`;
}

export class EventEmitter {
  #callbacks = {};

  static Event = class {
    type = "";
    target = "";
    data = "";
  };

  addEventListener(eventType: string, callback: any) {
    if (typeof callback !== "function") return;
    // @ts-ignore
    if (this.#callbacks[eventType] === undefined) {
      // @ts-ignore
      this.#callbacks[eventType] = [];
    }

    // @ts-ignore
    this.#callbacks[eventType].push(callback);
  }

  // @ts-ignore
  dispatchEvent(eventType, data) {
    // @ts-ignore
    if (this.#callbacks[eventType] === undefined) return;

    const event = new EventEmitter.Event();
    event.type = eventType;
    // @ts-ignore
    event.target = this;
    event.data = data;

    // @ts-ignore
    this.#callbacks[eventType].forEach((callback) => {
      callback(event);
    });
  }
}

export function formatValue(num: number, fractionalDigits: number) {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: fractionalDigits,
    maximumFractionDigits: fractionalDigits,
  }).format(num);
}

export const baseUrl = process.env.VERCEL_PROJECT_PRODUCTION_URL
  ? `https://${process.env.VERCEL_PROJECT_PRODUCTION_URL}`
  : "http://localhost:3000";

export const createUrl = (
  pathname: string,
  params: URLSearchParams | ReadonlyURLSearchParams,
) => {
  const paramsString = params.toString();
  const queryString = `${paramsString.length ? "?" : ""}${paramsString}`;

  return `${pathname}${queryString}`;
};

export const ensureStartsWith = (stringToCheck: string, startsWith: string) =>
  stringToCheck.startsWith(startsWith)
    ? stringToCheck
    : `${startsWith}${stringToCheck}`;

export const validateEnvironmentVariables = () => {
  const requiredEnvironmentVariables = [
    "SHOPIFY_STORE_DOMAIN",
    "SHOPIFY_STOREFRONT_ACCESS_TOKEN",
    "SHOPIFY_REVALIDATION_SECRET",
    "SHOPIFY_WEBHOOK_SIGNATURE",
  ];
  const missingEnvironmentVariables = [] as string[];

  requiredEnvironmentVariables.forEach((envVar) => {
    if (!process.env[envVar]) {
      missingEnvironmentVariables.push(envVar);
    }
  });

  if (missingEnvironmentVariables.length) {
    throw new Error(
      `The following environment variables are missing. Your site will not work without them. Read more: https://vercel.com/docs/integrations/shopify#configure-environment-variables\n\n${missingEnvironmentVariables.join(
        "\n",
      )}\n`,
    );
  }

  if (
    process.env.SHOPIFY_STORE_DOMAIN?.includes("[") ||
    process.env.SHOPIFY_STORE_DOMAIN?.includes("]")
  ) {
    throw new Error(
      "Your `SHOPIFY_STORE_DOMAIN` environment variable includes brackets (ie. `[` and / or `]`). Your site will not work with them there. Please remove them.",
    );
  }
};

export const encodeIdToBase64 = (id: string) =>
  btoa(id).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");

export const decodeIdToBase64 = (encoded: string) =>
  atob(encoded.replace(/-/g, "+").replace(/_/g, "/"));
